import type { OrganizationRole } from 'src/api/main'

import type { InviteState, UserStatus, WorkspaceUser } from '../types'

/**
 * Derives the display status from `OrganizationUsers.status` (free-form string
 * from the API) with sensible fallbacks. The API may return values like
 * `"active"`, `"invited"`, `"blocked"`, `"pending"`, `"inactive"`.
 */
export const deriveUserStatus = (record: WorkspaceUser): UserStatus => {
  const raw = (record.status ?? '').toLowerCase()
  if (raw.includes('block') || raw.includes('suspend')) return 'suspended'
  if (raw.includes('invite') || raw.includes('pending')) return 'invited'
  if (raw === 'inactive' || raw === 'disabled') return 'inactive'
  if (!record.user.last_login) return 'invited'
  return 'active'
}

/**
 * Invite lifecycle state for a member. Prefers the backend `invite_state`
 * field; falls back to deriving from `last_login` so the UI still works against
 * an older API that doesn't return the field yet.
 */
export const deriveInviteState = (
  record: WorkspaceUser
): InviteState | null => {
  if (record.invite_state) return record.invite_state
  if (record.user.last_login) return 'accepted'
  return 'pending'
}

/** True when a member still has a not-yet-accepted invite (resend/revoke apply). */
export const isPendingInvite = (record: WorkspaceUser): boolean => {
  const state = deriveInviteState(record)

  return state === 'pending' || state === 'expired'
}

/** Which admin MFA actions to surface for a member. */
export type MfaMenuActions = {
  /** Offer "Require MFA" — this workspace does not yet compel MFA for the member. */
  requireMfa: boolean
  /** Offer "Stop requiring MFA" — this workspace currently compels MFA for the member. */
  disableMfa: boolean
  /** Offer "Reset MFA" (recovery) — the member has enrolled a factor and accepted. */
  resetMfa: boolean
}

/**
 * Decides which MFA admin actions a member's menu should show.
 *
 * The require/disable toggle keys off `mfa_required` — the workspace requirement
 * that `require-mfa`/`disable-mfa` actually change — NOT `mfa_enabled` (the
 * member's own enrollment, which no admin action flips). Gating the toggle on
 * `mfa_enabled` is the bug that made "Disable" a no-op (the response keeps
 * `mfa_enabled: true`) and left "Require" showing on already-required members.
 *
 * `reset-mfa` is account recovery and only applies once the member has actually
 * enrolled a factor and accepted their invite. Both flags arrive `null` for
 * non-admin viewers, so every action is hidden for them.
 */
export const deriveMfaActions = (
  record: WorkspaceUser,
  accepted: boolean
): MfaMenuActions => ({
  requireMfa: record.mfa_required === false,
  disableMfa: record.mfa_required === true,
  resetMfa: record.mfa_enabled === true && accepted
})

export const ROLE_PALETTE: Record<
  OrganizationRole,
  { bg: string; color: string }
> = {
  owner: {
    bg: 'var(--ant-color-error-bg)',
    color: 'var(--ant-color-error)'
  },
  admin: {
    bg: 'var(--ant-color-warning-bg)',
    color: 'var(--ant-color-warning)'
  },
  employee: {
    bg: 'var(--ant-color-primary-bg)',
    color: 'var(--ant-color-primary-text)'
  }
}

export const STATUS_PALETTE: Record<
  UserStatus,
  { bg: string; color: string; dot: string }
> = {
  active: {
    bg: 'var(--ant-color-success-bg)',
    color: 'var(--ant-color-success)',
    dot: 'var(--ant-color-success)'
  },
  invited: {
    bg: 'var(--ant-color-warning-bg)',
    color: 'var(--ant-color-warning)',
    dot: 'var(--ant-color-warning)'
  },
  suspended: {
    bg: 'var(--ant-color-error-bg)',
    color: 'var(--ant-color-error)',
    dot: 'var(--ant-color-error)'
  },
  inactive: {
    bg: 'var(--ant-color-fill-secondary)',
    color: 'var(--ant-color-text-secondary)',
    dot: 'var(--ant-color-text-quaternary)'
  }
}

// Re-exported from src/utils/string so the shared helper is reachable as
// `composeDisplayName` from both the users feature and the broader app.
export { composeDisplayName } from 'src/utils/string'
